import { useCallback } from 'react';

import { Logger } from '../../../../logger/Logger';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useSettingsSlice } from '../selectors';
import { settingsActions } from '../slice';

export interface VolumeControlType {
  change: (value: number) => void;
  temporaryChange: (value: number | null) => void;
  toggle: () => void;
  unmuteVideo: () => void;
  value: number;
}

const unmuteVideo = (): void => {
  const video = document.getElementsByTagName('video')[0];
  if (video) {
    video.muted = false;
  }
};

export const useMasterVolumeControl = (): VolumeControlType => {
  const dispatch = useCoreDispatch();
  const volumeValue = useSettingsSlice.getMasterChannelValue();
  const channel = useSettingsSlice.getMasterChannel();

  const change = (value: number): void => {
    dispatch(settingsActions.setMasterVolume(value));
  };
  const temporaryChange = (value: number | null): void => {
    dispatch(settingsActions.setMasterVolumeTemporary(value));
  };

  const toggle = (): void => {
    dispatch(settingsActions.setMasterVolume(volumeValue ? 0 : channel.previousValue));
  };

  return { change, temporaryChange, toggle, unmuteVideo, value: volumeValue };
};

export const useVolumeControl = (channelName: string): VolumeControlType | null => {
  return useVolumeControlGetter()(channelName);
};

export const useVolumeControlGetter = (): ((channelName: string) => VolumeControlType | null) => {
  const dispatch = useCoreDispatch();
  const getRawValue = useSettingsSlice.getVolumeChannelRawValue();
  const getVolumeChannel = useSettingsSlice.getVolumeChannel();
  const masterVolume = useSettingsSlice.getMasterChannel();
  const noVolumeControl = useSettingsSlice.getNoVolumeControl();
  const allIsMuted = useSettingsSlice.getAllIsMutedCheck();

  return useCallback(
    (channelName: string): VolumeControlType | null => {
      const volumeValue = getRawValue(channelName);
      const channel = getVolumeChannel(channelName);

      if (!channel) {
        return null;
      }

      const change = (value: number): void => {
        dispatch(settingsActions.setVolume({ channel: channelName, value }));
        if ((noVolumeControl && value > 0 && masterVolume.value === 0) || (allIsMuted && value > 0)) {
          dispatch(settingsActions.setMasterVolumeOnly(1));
        }
      };
      const temporaryChange = (value: number | null): void => {
        dispatch(settingsActions.setVolumeTemporary({ channel: channelName, value }));
      };

      const toggle = (): void => {
        const previousValue = channel.previousValue === 0 ? 1 : channel.previousValue;
        const volume = volumeValue ? 0 : previousValue;
        dispatch(settingsActions.setVolume({ channel: channelName, value: volume }));
        if (allIsMuted && volume > 0) {
          dispatch(settingsActions.setMasterVolumeOnly(1));
        }
        Logger.logEvent(
          'click',
          `settingsVolumeToggle-${channelName}`,
          Date.now(),
          1,
          volume === 0 ? 'off' : 'on',
          false
        );
      };

      return { change, temporaryChange, toggle, unmuteVideo, value: volumeValue };
    },
    [dispatch, getRawValue, getVolumeChannel, allIsMuted] // todo: review dependencies
  );
};
